import { useCallback, useEffect, useRef, useState } from 'react';
import { useSupabaseClient } from '@supabase/auth-helpers-react';
import { errorMessage, notifyReadOnly } from '../../../../utils/supabaseErrors';
import {
  EmployeeLeave,
  LeaveAdjustmentInput,
  LeaveEntitlementInput
} from '../../config';

export interface LeaveProfileInput {
  accrual_start_date: string | null;
  opening_annual_hours: number;
  opening_sick_hours: number;
}

export interface EmployeeLeaveState {
  data: EmployeeLeave | null;
  loading: boolean;
  error: string | null;
  reload: () => Promise<void>;
  /** Each write resolves to an error message, or null when it worked. */
  saveProfile: (input: LeaveProfileInput) => Promise<string | null>;
  /** Adds an entitlement, or replaces the one that starts on the same date. */
  saveEntitlement: (input: LeaveEntitlementInput) => Promise<string | null>;
  deleteEntitlement: (id: number) => Promise<string | null>;
  addAdjustment: (input: LeaveAdjustmentInput) => Promise<string | null>;
  deleteAdjustment: (id: number) => Promise<string | null>;
}

/**
 * One employee's leave: the read, and every write.
 *
 * Accruals are worked out by the database each time this is read, so there is
 * nothing to keep in step here: a write is always followed by a reload, and
 * the balance on screen is whatever the database now says.
 */
export const useEmployeeLeave = (employeeId: string | null, enabled: boolean): EmployeeLeaveState => {
  const supabase = useSupabaseClient();
  const [data, setData] = useState<EmployeeLeave | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  // A slow response for the employee opened before this one must not land.
  const requestRef = useRef(0);

  const load = useCallback(async (showLoading: boolean) => {
    if (!employeeId) return;
    const request = ++requestRef.current;
    if (showLoading) setLoading(true);
    setError(null);

    const { data: result, error: rpcError } = await supabase.rpc('get_employee_leave', {
      p_employee_id: employeeId
    });

    if (request !== requestRef.current) return;
    if (rpcError) {
      console.error('Error loading leave:', rpcError);
      setError(errorMessage(rpcError, 'Leave could not be loaded.'));
      setData(null);
    } else {
      setData(result as EmployeeLeave);
    }
    setLoading(false);
  }, [employeeId, supabase]);

  useEffect(() => {
    if (!enabled || !employeeId) {
      requestRef.current++;
      setData(null);
      setError(null);
      setLoading(false);
      return;
    }
    load(true);
  }, [enabled, employeeId, load]);

  const reload = useCallback(() => load(false), [load]);

  const write = useCallback(async (
    fn: string,
    args: Record<string, unknown>,
    fallback: string
  ): Promise<string | null> => {
    const { error: rpcError } = await supabase.rpc(fn, args);
    if (rpcError) {
      console.error(`Error calling ${fn}:`, rpcError);
      notifyReadOnly(rpcError);
      return errorMessage(rpcError, fallback);
    }
    await load(false);
    return null;
  }, [supabase, load]);

  const saveProfile = useCallback((input: LeaveProfileInput) => write('set_leave_profile', {
    p_employee_id: employeeId,
    p_accrual_start_date: input.accrual_start_date,
    p_opening_annual_hours: input.opening_annual_hours,
    p_opening_sick_hours: input.opening_sick_hours
  }, 'The opening balance could not be saved.'), [write, employeeId]);

  const saveEntitlement = useCallback((input: LeaveEntitlementInput) => write('set_leave_entitlement', {
    p_employee_id: employeeId,
    p_effective_from: input.effective_from,
    p_contracted_hours_per_week: input.contracted_hours_per_week,
    p_annual_leave_weeks_per_year: input.annual_leave_weeks_per_year,
    p_sick_leave_days_per_year: input.sick_leave_days_per_year,
    p_accrues: input.accrues,
    p_note: input.note
  }, 'The entitlement could not be saved.'), [write, employeeId]);

  const deleteEntitlement = useCallback((id: number) => write('delete_leave_entitlement', {
    p_id: id
  }, 'The entitlement could not be removed.'), [write]);

  const addAdjustment = useCallback((input: LeaveAdjustmentInput) => write('add_leave_adjustment', {
    p_employee_id: employeeId,
    p_leave_type: input.leave_type,
    p_hours: input.hours,
    p_effective_date: input.effective_date,
    p_note: input.note
  }, 'The adjustment could not be saved.'), [write, employeeId]);

  const deleteAdjustment = useCallback((id: number) => write('delete_leave_adjustment', {
    p_id: id
  }, 'The adjustment could not be removed.'), [write]);

  return {
    data,
    loading,
    error,
    reload,
    saveProfile,
    saveEntitlement,
    deleteEntitlement,
    addAdjustment,
    deleteAdjustment
  };
};

export default useEmployeeLeave;
